import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CatalogSearchResponse, CatalogSearchResult } from '../../../data/catalogSearch';
import { catalogQueryOptions } from '../queries';
import { toSearchRows } from '../../../lib/catalogMap';
import { searchCatalogRanked } from '../repository';
import {
  catalogPhase,
  keywordFilter,
  type CatalogPhase,
} from '../../../lib/catalogSearch';

/** How many instant keyword rows to show, and how many ranked results to ask
 *  the gateway for. */
const KEYWORD_CAP = 8;
const RESULT_LIMIT = 8;

/** The palette's discrete phases, each with its own body in the mockup. Kept as
 *  a local alias so existing importers keep resolving `SearchPhase`. */
export type SearchPhase = CatalogPhase;

export interface CatalogSearchState {
  loading: boolean;
  loadError: Error | null;
  retry: () => void;
  /** total number of loaded catalog endpoints (for the honest "N total" count) */
  catalogCount: number;
  query: string;
  setQuery: (q: string) => void;
  phase: SearchPhase;
  /** rows to render + navigate for the active phase (popular / keyword / results) */
  rows: CatalogSearchResult[];
  /** honest matched count from the last search (results phase) */
  total: number;
  /** ranker used for the last completed gateway search */
  ranking: CatalogSearchResponse['ranking'];
  searchError: Error | null;
  sel: number;
  setSel: (i: number) => void;
  moveSel: (delta: number) => void;
  /** runs the semantic search for `q` (defaults to the current query) */
  runSearch: (q?: string) => void;
  /** clears the query and any search back to the empty state */
  reset: () => void;
}

/**
 * Owns the command palette's state: it loads the flat catalog once (cached, so
 * opening the palette costs no request), filters it instantly for keyword mode,
 * and runs the gateway's use-case search on submit. Keeps the parent palette
 * component thin - the phase machine and the stale-response guard live here.
 */
export const useCatalogSearch = (): CatalogSearchState => {
  const catalogQuery = useQuery({
    ...catalogQueryOptions(),
    select: toSearchRows,
  });
  const catalog = useMemo(
    () => (catalogQuery.error ? [] : (catalogQuery.data ?? [])),
    [catalogQuery.data, catalogQuery.error],
  );

  const [query, setQueryRaw] = useState('');
  const [sel, setSel] = useState(0);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<Error | null>(null);
  const [results, setResults] = useState<CatalogSearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [ranking, setRanking] = useState<CatalogSearchResponse['ranking']>('semantic');
  const runIdRef = useRef(0);

  const keywordRows = useMemo(
    () => keywordFilter(catalog, query).slice(0, KEYWORD_CAP),
    [catalog, query],
  );

  const trimmed = query.trim();
  const phase = catalogPhase({
    trimmed,
    submitted,
    searching,
    hasSearchError: searchError !== null,
  });

  // The empty phase surfaces nothing (progressive disclosure); results show the
  // ranked hits; keyword and error fall back to the instant keyword matches so
  // the palette still does something useful when the semantic endpoint is down.
  const rows =
    phase === 'results'
      ? results
      : phase === 'keyword' || phase === 'error'
        ? keywordRows
        : [];

  const setQuery = useCallback((q: string) => {
    setQueryRaw(q);
    setSel(0);
  }, []);

  const runSearch = useCallback(
    (q?: string) => {
      const text = (q ?? query).trim();
      if (!text) return;
      if (q !== undefined) setQueryRaw(q);
      setSubmitted(text);
      setSearching(true);
      setSearchError(null);
      setSel(0);
      const runId = ++runIdRef.current;
      searchCatalogRanked(text, { limit: RESULT_LIMIT })
        .then((res) => {
          if (runId !== runIdRef.current) return;
          setResults(res.results);
          setTotal(res.total);
          setRanking(res.ranking);
          setSearching(false);
        })
        .catch((e: unknown) => {
          if (runId !== runIdRef.current) return;
          setSearchError(e instanceof Error ? e : new Error(String(e)));
          setSearching(false);
        });
    },
    [query],
  );

  const moveSel = useCallback(
    (delta: number) =>
      setSel((i) => {
        const n = rows.length;
        if (n === 0) return 0;
        return (i + delta + n) % n;
      }),
    [rows.length],
  );

  const reset = useCallback(() => {
    runIdRef.current++;
    setQueryRaw('');
    setSel(0);
    setSubmitted(null);
    setSearching(false);
    setSearchError(null);
    setResults([]);
    setTotal(0);
    setRanking('semantic');
  }, []);

  return {
    loading: catalogQuery.isPending,
    loadError: catalogQuery.error,
    retry: () => {
      void catalogQuery.refetch();
    },
    catalogCount: catalog.length,
    query,
    setQuery,
    phase,
    rows,
    total,
    ranking,
    searchError,
    sel,
    setSel,
    moveSel,
    runSearch,
    reset,
  };
};
